import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import ErrorBoundary from "./components/ErrorBoundary.tsx";
import "./globals.css";

// Note: Colors are embedded in globals.css at build time
// No runtime palette loading needed!

// Global error handlers to catch all unhandled errors
window.onerror = (message, source, lineno, colno, error) => {
  console.error("Global error:", {
    message,
    source,
    lineno,
    colno,
    error,
  });
  
  // Prepare error details for AI fix
  const errorDetails = JSON.stringify({
    message: String(message),
    source: source || undefined,
    lineno,
    colno,
    stack: error?.stack || undefined,
  });
  
  // Display error on page if no error boundary caught it
  const errorDiv = document.createElement("div");
  errorDiv.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #1a1a1a;
    color: #ff4444;
    padding: 20px;
    font-family: monospace;
    overflow: auto;
    z-index: 9999;
  `;
  errorDiv.innerHTML = `
    <h1 style="color: #ff4444;">⚠️ Uncaught Error</h1>
    <div style="background: #2a2a2a; padding: 15px; border-radius: 5px; margin: 20px 0; border: 1px solid #ff4444;">
      <h2 style="color: #ffaa44; margin-top: 0;">Error Message:</h2>
      <pre style="white-space: pre-wrap; color: white;">${message}</pre>
    </div>
    ${source ? `
      <div style="background: #2a2a2a; padding: 15px; border-radius: 5px; margin: 20px 0; border: 1px solid #ff4444;">
        <h2 style="color: #ffaa44; margin-top: 0;">Source:</h2>
        <pre style="white-space: pre-wrap; color: #cccccc;">${source}:${lineno}:${colno}</pre>
      </div>
    ` : ''}
    ${error?.stack ? `
      <div style="background: #2a2a2a; padding: 15px; border-radius: 5px; margin: 20px 0; border: 1px solid #ff4444;">
        <h2 style="color: #ffaa44; margin-top: 0;">Stack Trace:</h2>
        <pre style="white-space: pre-wrap; font-size: 12px; color: #cccccc;">${error.stack}</pre>
      </div>
    ` : ''}
    <div style="display: flex; gap: 10px;">
      <button onclick="window.location.reload()" style="
        padding: 10px 20px;
        background: #2563EB;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        font-weight: bold;
      ">Reload Page</button>
      <button onclick="window.parent.postMessage({type: 'EPISOLO_ERROR', error: ${errorDetails.replace(/"/g, '&quot;')}}, '*')" style="
        padding: 10px 20px;
        background: #059669;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        font-weight: bold;
      ">Fix with AI</button>
    </div>
  `;
  document.body.appendChild(errorDiv);
  
  return false;
};

// Catch unhandled promise rejections
window.onunhandledrejection = (event) => {
  console.error("Unhandled promise rejection:", event.reason);
  
  // Prepare error details for AI fix
  const errorDetails = JSON.stringify({
    message: event.reason?.toString() || String(event.reason),
    stack: event.reason?.stack || undefined,
  });
  
  const errorDiv = document.createElement("div");
  errorDiv.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #1a1a1a;
    color: #ff4444;
    padding: 20px;
    font-family: monospace;
    overflow: auto;
    z-index: 9999;
  `;
  errorDiv.innerHTML = `
    <h1 style="color: #ff4444;">⚠️ Unhandled Promise Rejection</h1>
    <div style="background: #2a2a2a; padding: 15px; border-radius: 5px; margin: 20px 0; border: 1px solid #ff4444;">
      <h2 style="color: #ffaa44; margin-top: 0;">Error:</h2>
      <pre style="white-space: pre-wrap; color: white;">${event.reason?.toString() || event.reason}</pre>
    </div>
    ${event.reason?.stack ? `
      <div style="background: #2a2a2a; padding: 15px; border-radius: 5px; margin: 20px 0; border: 1px solid #ff4444;">
        <h2 style="color: #ffaa44; margin-top: 0;">Stack Trace:</h2>
        <pre style="white-space: pre-wrap; font-size: 12px; color: #cccccc;">${event.reason.stack}</pre>
      </div>
    ` : ''}
    <div style="display: flex; gap: 10px;">
      <button onclick="window.location.reload()" style="
        padding: 10px 20px;
        background: #2563EB;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        font-weight: bold;
      ">Reload Page</button>
      <button onclick="window.parent.postMessage({type: 'EPISOLO_ERROR', error: ${errorDetails.replace(/"/g, '&quot;')}}, '*')" style="
        padding: 10px 20px;
        background: #059669;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        font-weight: bold;
      ">Fix with AI</button>
    </div>
  `;
  document.body.appendChild(errorDiv);
};

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
